import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Badge, Button, Card, Spinner } from 'react-bootstrap';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { DataTable } from '../components/common/DataTable';
import { renderWrappedText } from '../components/common/DataTable/cellRenderers';
import type { ColumnConfig } from '../components/common/DataTable/types';
import { FilterBar } from '../components/common/FilterControls';
import type { FilterDefinition } from '../components/common/FilterControls';
import { CopyableText } from '../components/common/Copyable';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { RootState } from '../store';

import {
  getCommissionsDiff,
  postCommissionsDiffRecompute,
  putCommissionsDiffReview,
  useGetUsersId,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  CommissionDiff,
  GetCommissionsDiff200,
  GetCommissionsDiffParams,
  CommissionDiffReviewWriteStatus,
} from '../api/generated/model';

import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { useSyncState } from '../hooks/useSyncState';
import { SyncButton } from '../components/SyncButton';

const COMMISSIONS_ENGINE_INTEGRATION_ID = 'commissions-engine';

const FETCH_LIMIT = 50;

// Category labels — short forms displayed inline; full text in tooltip.
const CATEGORY_LABELS: Record<string, { short: string; long: string }> = {
  amount_delta: {
    short: 'amount',
    long: 'commission_amount differs between engines.',
  },
  status_changed: {
    short: 'status',
    long: 'Status pill differs between engines (e.g. old "partially_due" → new "due"). Emitted on ANY status string difference so the impact of the 7→4 collapse is visible per row.',
  },
  missing_quota_changed: {
    short: 'missing quota',
    long: 'MissingQuota flag differs between engines.',
  },
  paid_so_far_mismatch: {
    short: 'paid mismatch',
    long: 'commission_paid_so_far differs. Should be impossible — both engines sum the same commission_payment_invoices table. If you see this, it is a bug to chase.',
  },
  due_mismatch: {
    short: 'due mismatch',
    long: 'commission_due differs even though amount and paid match. Almost certainly a derived-field bug if it ever fires.',
  },
  new_only: {
    short: 'new only',
    long: 'New engine has a row for this (invoice, user, role), old engine has nothing.',
  },
  old_only: {
    short: 'old only',
    long: 'Old engine has a row for this (invoice, user, role), new engine has nothing.',
  },
  walk_order_reattribution: {
    short: 'different invoice',
    long: 'The engines agree on the total commission for this user this period — they disagree about which specific invoice attracts the over-quota dollars. See the row’s explanation for which invoice each engine chose and why.',
  },
  bug_a: {
    short: 'bug A (late on last day)',
    long: 'Old-engine period-bound off-by-one: the live page excludes invoices booked on the last day of a month after 00:00 UTC because the period filter uses `booking_date < endRFC` with endRFC at midnight UTC. The invoice falls into a gap between months and never appears on the live page. New engine catches it via per-invoice scan.',
  },
  bug_b: {
    short: 'bug B (midnight + neg TZ)',
    long: 'Old-engine SQLite lex-sort bug: stored booking_date has a negative timezone offset (e.g. `-08:00`); SQLite compares the TEXT column lexicographically and `-XX:XX` sorts before `Z`. When local time is midnight on the first of a month, the row falls below the period\'s lower bound and is dropped. New engine compares as time.Time in-memory, sidestepping the lex bug.',
  },
};

const formatCurrency = (s?: string | null): string => {
  if (!s) return '';
  const n = parseFloat(s);
  if (Number.isNaN(n)) return s;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
};

// formatPeriodMonth renders a calendar-date string (YYYY-MM-DD) as
// "YYYY-MM". Splits the string directly so the year/month components
// are read as-is — no Date() interpretation, no UTC-midnight trap,
// no viewer-timezone shift. Period dates from the commissions stack
// are calendar dates in the business timezone; treating them with
// timezone math is wrong.
const formatPeriodMonth = (s?: string | null | object): string => {
  if (!s) return '';
  const str = String(s);
  const parts = str.split('-');
  if (parts.length < 2 || parts[0].length !== 4 || parts[1].length !== 2) {
    return str;
  }
  return `${parts[0]}-${parts[1]}`;
};

// statusBadge renders any status pill — both the new engine's
// five-state values (not_due / due / paid / overpaid / prepaid) and the
// old engine's seven-state values (which add partially_due / partial /
// partially_paid). Unknown strings fall back to a generic chip so the
// table doesn't go blank if a future state lands.
//
// Color semantics: success = settled, warning = some action still owed,
// info = prepaid (money out before sale earnable — informational), danger
// = overpaid (money out beyond what's owed — operator audit signal),
// secondary = inert/no-state.
const statusBadge = (s?: string | null) => {
  switch (s) {
    case 'paid':           return <Badge bg="success">Paid</Badge>;
    case 'due':            return <Badge bg="warning" text="dark">Due</Badge>;
    case 'overpaid':       return <Badge bg="danger">Overpaid</Badge>;
    case 'prepaid':        return <Badge bg="info">Prepaid</Badge>;
    case 'not_due':        return <Badge bg="secondary">Not Due</Badge>;
    case 'partially_due':  return <Badge bg="warning" text="dark">Partially Due</Badge>;
    case 'partially_paid': return <Badge bg="success">Partially Paid</Badge>;
    case 'partial':        return <Badge bg="warning" text="dark">Partial</Badge>;
    default:               return s ? <Badge bg="light" text="dark">{s}</Badge> : null;
  }
};

const reviewBadge = (s?: string | null) => {
  switch (s) {
    case 'expected':    return <Badge bg="success">Expected</Badge>;
    case 'investigate': return <Badge bg="danger">Investigate</Badge>;
    case 'pending':     return <Badge bg="secondary">Pending</Badge>;
    default:            return <Badge bg="secondary">Pending</Badge>;
  }
};

// rowKey — module-level so its reference is stable. Matches the
// canonical InvoicesPage pattern (getRowKey from useCallback with empty
// deps would also work — both produce a stable reference).
const rowKey = (d: CommissionDiff): string => d.id ?? '';

const CommissionsDiffPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { data: userData } = useGetUsersId(user?.id || '', { query: { enabled: !!user?.id } });
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<{
    id?: string;
    user_name?: string;
    review_status?: string;
    category?: string;
  }>(() => {
    // category preserves '' from the URL (deselect-all sentinel).
    // searchParams.get returns null for missing, '' for present-but-empty.
    const cat = searchParams.get('category');
    return {
      id: searchParams.get('id') || undefined,
      user_name: searchParams.get('user_name') || undefined,
      review_status: searchParams.get('review_status') || undefined,
      category: cat === null ? undefined : cat,
    };
  });

  const [showFilterBar, setShowFilterBar] = useState(false);
  const activeFilterCount =
    (filters.id ? 1 : 0) +
    (filters.user_name ? 1 : 0) +
    (filters.review_status ? 1 : 0) +
    // category: '' (deselect-all) counts as active; only undefined is "no filter".
    (filters.category !== undefined ? 1 : 0);

  const [sortField, setSortField] = useState(searchParams.get('sort_field') || 'period_start');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    (searchParams.get('sort_direction') as 'asc' | 'desc') || 'desc'
  );

  // URL sync — same conditional pattern as InvoicesPage,
  // BankTransactionsPage, EventsNew, etc.: persist non-empty filters
  // and non-default sort. Defaults are omitted so the URL stays clean
  // for the common case. Exception: category is a multi-select where
  // '' (deselect-all sentinel) must round-trip through the URL —
  // otherwise the "Deselect all" state degrades to "no filter" on
  // refresh.
  useEffect(() => {
    const p = new URLSearchParams();
    if (filters.id) p.set('id', filters.id);
    if (filters.user_name) p.set('user_name', filters.user_name);
    if (filters.review_status) p.set('review_status', filters.review_status);
    if (filters.category !== undefined) p.set('category', filters.category);
    if (sortField !== 'period_start') p.set('sort_field', sortField);
    if (sortDir !== 'desc') p.set('sort_direction', sortDir);
    setSearchParams(p, { replace: true });
  }, [filters, sortField, sortDir, setSearchParams]);

  // Tracks rows whose review save is in-flight; surfaces a spinner per row.
  const [savingRowKeys, setSavingRowKeys] = useState<Set<string>>(new Set());

  // Invoice detail modal opens when the operator clicks an invoice
  // number cell. invoiceModalId is the composite "{src}-{id}" the
  // InvoiceDetailModal expects.
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      return {
        ...(filters.id ? { id: filters.id } : {}),
        ...(filters.user_name ? { user_name: filters.user_name } : {}),
        ...(filters.review_status ? { review_status: filters.review_status } : {}),
        // category preserves '' (deselect-all → match nothing). undefined = no filter.
        ...(filters.category !== undefined ? { category: filters.category } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
      };
    },
    [filters, sortField, sortDir]
  );

  const {
    allItems,
    totalCount,
    isLoading,
    hasMore,
    handleLoadMore,
    reset,
    error: paginationError,
  } = usePaginatedQuery<CommissionDiff, GetCommissionsDiff200, unknown>({
    queryKey: 'commissions-diff',
    buildQueryParams,
    queryFn: (params) => getCommissionsDiff(params as GetCommissionsDiffParams),
    getRowKey: rowKey,
  });

  // Regenerate-button → SSE → refresh-on-complete plumbing.
  // useSyncState returns the most recent sync event; the same completed
  // event survives across multiple re-renders (until the
  // NotificationContext evicts it). Track the last sync_id we already
  // handled so reset() fires exactly once per completed sync.
  const recomputeState = useSyncState(COMMISSIONS_ENGINE_INTEGRATION_ID);
  const handledRecomputeSyncIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!recomputeState || recomputeState.status !== 'completed') return;
    const sid = recomputeState.sync_id;
    if (!sid || handledRecomputeSyncIdRef.current === sid) return;
    handledRecomputeSyncIdRef.current = sid;
    reset();
  }, [recomputeState, reset]);
  const handleRecomputeClick = useCallback(async (): Promise<string | undefined> => {
    try {
      const resp = await postCommissionsDiffRecompute();
      return (resp as any)?.sync_id;
    } catch (err) {
      console.error('commissions recompute trigger failed:', err);
      return undefined;
    }
  }, []);

  const handleSetReview = useCallback(
    async (row: CommissionDiff, status: CommissionDiffReviewWriteStatus) => {
      if (!row.invoice_src_id || !row.invoice_id || !row.user_id || !row.role) return;
      const key = rowKey(row);
      setSavingRowKeys(prev => {
        const n = new Set(prev);
        n.add(key);
        return n;
      });
      try {
        await putCommissionsDiffReview({
          invoice_src_id: row.invoice_src_id,
          invoice_id: row.invoice_id,
          user_id: row.user_id,
          role: row.role as any,
          status,
        });
        // Force re-fetch from page 1 so the row reflects the new review
        // state. reset() removes the query cache + clears local state,
        // which triggers a single fresh page-1 fetch — invalidate is
        // redundant.
        reset();
      } finally {
        setSavingRowKeys(prev => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [reset]
  );

  const columns = useMemo<ColumnConfig<CommissionDiff>[]>(
    () => [
      {
        key: 'id',
        label: 'Diff ID',
        width: 120,
        sortable: false,
        renderCell: (d) => {
          if (!d.id) return '';
          return (
            <CopyableText
              as="code"
              value={d.id}
              className="small text-muted d-inline-block w-100 text-truncate"
              title={`Click to copy ${d.id}`}
            />
          );
        },
      },
      {
        key: 'invoice_number',
        label: 'Invoice',
        width: 110,
        sortable: true,
        renderCell: (d) => {
          if (!d.invoice_number) return '';
          return (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-decoration-none"
              onClick={(e) => {
                e.stopPropagation();
                if (d.invoice_src_id && d.invoice_id) {
                  setInvoiceModalId(`${d.invoice_src_id}-${d.invoice_id}`);
                }
              }}
              title="Open invoice details"
            >
              {d.invoice_number}
            </Button>
          );
        },
      },
      {
        key: 'customer_name',
        label: 'Customer',
        width: 200,
        sortable: true,
        renderCell: (d) => d.customer_name ?? '',
      },
      {
        key: 'user_name',
        label: 'User',
        width: 160,
        sortable: true,
        renderCell: (d) => d.user_name ?? d.user_id ?? '',
      },
      {
        key: 'role',
        label: 'Role',
        width: 110,
        sortable: true,
        renderCell: (d) =>
          d.role === 'sales_lead' ? <Badge bg="info">Sales Lead</Badge>
          : d.role === 'salesperson' ? <Badge bg="primary">Salesperson</Badge>
          : (d.role ?? ''),
      },
      {
        key: 'period_start',
        label: 'Period',
        width: 100,
        sortable: true,
        // period_start is a calendar date string (YYYY-MM-DD) representing
        // a date in the business timezone — no time component, no timezone
        // shift expected. Parse by splitting on '-' and treat year/month
        // as pure numerics (same idiom as CommissionsPage.monthLabel).
        // Never call new Date() on it — that interprets as UTC midnight
        // and renders as the prior day in negative-UTC viewer timezones.
        renderCell: (d) => formatPeriodMonth(d.period_start),
      },
      {
        key: 'old_engine_amount',
        label: 'Old Engine $',
        width: 120,
        sortable: true,
        renderCell: (d) => formatCurrency(d.old_engine_amount),
      },
      {
        key: 'commission_amount',
        label: 'New Engine $',
        width: 120,
        sortable: true,
        renderCell: (d) => formatCurrency(d.commission_amount),
      },
      {
        key: 'delta',
        label: 'Delta (New − Old)',
        width: 140,
        sortable: true,
        renderCell: (d) => {
          const v = parseFloat(d.delta ?? '0');
          if (!Number.isFinite(v) || v === 0) return formatCurrency(d.delta);
          return (
            <span className={v > 0 ? 'text-success' : 'text-danger'}>
              {formatCurrency(d.delta)}
            </span>
          );
        },
      },
      {
        key: 'old_commission_paid_so_far',
        label: 'Old Paid $',
        width: 110,
        sortable: false,
        renderCell: (d) => formatCurrency(d.old_commission_paid_so_far),
      },
      {
        key: 'commission_paid_so_far',
        label: 'New Paid $',
        width: 110,
        sortable: true,
        renderCell: (d) => formatCurrency(d.commission_paid_so_far),
      },
      {
        key: 'old_status',
        label: 'Old Status',
        width: 140,
        sortable: true,
        renderCell: (d) => (
          <>
            {statusBadge(d.old_status)}
            {d.old_missing_quota && <Badge bg="warning" text="dark" className="ms-1">No Quota</Badge>}
          </>
        ),
      },
      {
        key: 'status',
        label: 'New Status',
        width: 120,
        sortable: true,
        renderCell: (d) => (
          <>
            {statusBadge(d.status)}
            {d.missing_quota && <Badge bg="warning" text="dark" className="ms-1">No Quota</Badge>}
          </>
        ),
      },
      {
        key: 'explanation',
        label: 'Explanation',
        width: 320,
        sortable: false,
        renderCell: (d) => renderWrappedText(d.explanation, 'small text-muted text-wrap'),
        tooltip: (d) => d.explanation || '',
      },
      {
        key: 'categories',
        label: 'Categories',
        width: 240,
        sortable: true,
        renderCell: (d) => {
          if (!d.categories || d.categories.length === 0) return '';
          return (
            <div className="d-flex flex-wrap gap-1">
              {d.categories.map((c, i) => {
                const meta = CATEGORY_LABELS[c as string];
                return (
                  <Badge
                    key={i}
                    bg="light"
                    text="dark"
                    title={meta?.long ?? c}
                  >
                    {meta?.short ?? c}
                  </Badge>
                );
              })}
            </div>
          );
        },
      },
      {
        key: 'review',
        label: 'Review',
        width: 300,
        sortable: false,
        renderCell: (d) => {
          const k = rowKey(d);
          const saving = savingRowKeys.has(k);
          const current = d.review?.status ?? 'pending';
          return (
            <div className="d-flex align-items-center gap-1">
              {reviewBadge(current)}
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Button
                    size="sm"
                    variant={current === 'expected' ? 'success' : 'outline-success'}
                    onClick={() => handleSetReview(d, 'expected')}
                    title="Mark this diff as expected — the new engine result is correct."
                  >
                    Expected
                  </Button>
                  <Button
                    size="sm"
                    variant={current === 'investigate' ? 'danger' : 'outline-danger'}
                    onClick={() => handleSetReview(d, 'investigate')}
                    title="Flag this diff for follow-up (data cleanup, true-up, escalation)."
                  >
                    Investigate
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [savingRowKeys, handleSetReview]
  );

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    // Text/select filters collapse '' → undefined (empty input == no filter).
    // Multi-select filters preserve '' as the "deselect all / match nothing"
    // sentinel — collapsing it would make Deselect all behave like Select all.
    setFilters(prev => {
      if (field === 'category') {
        return { ...prev, [field]: value };
      }
      return { ...prev, [field]: value || undefined };
    });
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  const filterDefs = useMemo((): FilterDefinition[] => [
    { type: 'text', field: 'id', label: 'Diff ID', icon: 'bi-hash', placeholder: 'Exact uuid...' },
    { type: 'text', field: 'user_name', label: 'User', icon: 'bi-person', placeholder: 'Search by name...' },
    {
      type: 'select', field: 'review_status', label: 'Review', icon: 'bi-check2-circle',
      options: [
        { value: 'pending',     label: 'Pending' },
        { value: 'expected',    label: 'Expected' },
        { value: 'investigate', label: 'Investigate' },
      ],
    },
    {
      type: 'multiselect', field: 'category', label: 'Category', icon: 'bi-tag',
      options: Object.entries(CATEGORY_LABELS).map(([k, meta]) => ({ value: k, label: meta.short })),
      // Diff rows carry MULTIPLE category tags simultaneously (e.g.
      // ['amount_delta','status_changed','due_mismatch']), so the
      // standard include-OR semantic of multi-select doesn't match
      // operator intent for "hide noise from these tags." Exclude mode
      // adds NOT-LIKE per checked category, giving the operator a real
      // way to suppress bug_a / bug_b noise without dropping legitimate
      // rows that also happen to carry one of the included tags.
      allowExclude: true,
    },
  ], []);

  return (
    <DashboardLayout title="Commissions Diff Audit" scrollable>
      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '240px', overflow: 'hidden' }}>
        <Card.Header className="bg-primary text-white flex-shrink-0">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <i className="bi bi-clipboard2-data me-1" />
            <div className="fw-bold flex-grow-0">Commissions v2 Diff</div>

            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilterBar(v => !v)}
              >
                <i className="bi bi-funnel me-1" />
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>

            <div className="ms-auto d-flex align-items-center gap-3 flex-wrap">
              <div className="bg-header-control rounded px-2 py-1">
                <SyncButton
                  claimId="commissions-engine-regenerate"
                  onClick={handleRecomputeClick}
                  variant="outline-primary"
                  buttonText="Regenerate"
                  icon="bi-arrow-clockwise"
                />
              </div>
              <div className="rounded px-3 py-1">
                <span className="text-white">
                  {totalCount !== undefined ? `${totalCount} diff${totalCount === 1 ? '' : 's'}` : ''}
                </span>
              </div>
            </div>
          </div>
          {showFilterBar && (
            <div className="mt-2">
              <FilterBar
                filters={filters}
                filterDefs={filterDefs}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}
        </Card.Header>

        {paginationError && (
          <div className="alert alert-danger m-2 mb-0 py-2 small" role="alert">
            {paginationError.message}
          </div>
        )}

        <Card.Body className="flex-grow-1 overflow-hidden p-0" style={{ minHeight: 0 }}>
          <DataTable
            data={allItems}
            columns={columns}
            getRowKey={rowKey}
            isLoading={isLoading}
            pagination={{ hasMore, limit: FETCH_LIMIT }}
            onLoadMore={handleLoadMore}
            sortConfig={{ field: sortField, direction: sortDir }}
            onSortChange={(field, direction) => {
              setSortField(field);
              setSortDir(direction);
            }}
            preferencesKey="commissions-diff"
            userId={user?.id}
            userPreferences={userData?.preferences}
            maxHeight="100%"
            enableStickyHeader={true}
            enableColumnReorder={true}
            enableColumnResize={true}
            enableColumnVisibility={true}
          />
        </Card.Body>
      </Card>

      <InvoiceDetailModal
        show={!!invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
        invoiceId={invoiceModalId}
      />
    </DashboardLayout>
  );
};

export default CommissionsDiffPage;
